import {
  HOST_TOOL_NAMES,
  WORKSPACE_TOOL_NAMES,
} from "../../turns/turn-constants";
import { labelOnly } from "../label-only";
import type { ToolActivity } from "../tool-activity";
import { toolActivityBasename } from "../tool-activity-basename";
import { toolActivityHostname } from "../tool-activity-hostname";
import { toolInput } from "../tool-input";
import type { ToolActivityState } from "../tool-activity-state";
import { browserToolActivityDescribers } from "../browser/browser-tool-activity-describers";
import { designToolActivityDescribers } from "../design/design-tool-activity-describers";
import { factoryToolActivityDescribers } from "../factory/factory-tool-activity-describers";
import { integrationToolActivityDescribers } from "./integration-tool-activity-describers";
import { describeAnnotateImage } from "../capture/describe-annotate-image";
import { describeWorkflow } from "./describe-workflow";
import { describeAttachImage } from "../capture/describe-attach-image";
import { describeGenerateImage } from "../capture/describe-generate-image";
import { describeRecordVideo } from "../capture/describe-record-video";
import { describeTakeScreenshot } from "../capture/describe-take-screenshot";
import { describeMemorySearch } from "./describe-memory-search";
import { describeReportDevServer } from "./describe-report-dev-server";
import { describeRequestEnvVars } from "./describe-request-env-vars";
import { describeAttachFile } from "../deliverable/describe-attach-file";
import { updateTaskLabels } from "./update-task-labels";
import { describeNativePatch } from "./describe-native-patch";

export const canonicalToolActivityDescribers: ReadonlyMap<
  string,
  (record: Record<string, unknown>, state: ToolActivityState) => ToolActivity
> = new Map([
  ["apply_patch", describeNativePatch],
  [HOST_TOOL_NAMES.ADVISE, () => labelOnly("Advised the watched session")],
  [
    "read",
    (record) =>
      labelOnly(
        `Read ${toolActivityBasename(toolInput.str(record.file_path) ?? toolInput.str(record.path)) ?? "a file"}`,
      ),
  ],
  [
    "write",
    (record) =>
      labelOnly(
        `Wrote ${toolActivityBasename(toolInput.str(record.file_path) ?? toolInput.str(record.path)) ?? "a file"}`,
      ),
  ],
  [
    "edit",
    (record) =>
      labelOnly(
        `Edited ${toolActivityBasename(toolInput.str(record.file_path) ?? toolInput.str(record.path)) ?? "a file"}`,
      ),
  ],
  [
    "bash",
    (record) => {
      // Native harnesses may omit either the intent or the command.
      const command =
        toolInput.verbatim(record.command) ?? toolInput.verbatim(record.cmd);
      const intent = toolInput.trimmed(record.description);
      if (!command && !intent) {
        return labelOnly("Ran a command");
      }
      return { text: intent, command };
    },
  ],
  [
    WORKSPACE_TOOL_NAMES.TASK_OUTPUT,
    () => labelOnly("Read a background command's output"),
  ],
  [
    WORKSPACE_TOOL_NAMES.TASK_STOP,
    () => labelOnly("Stopped a background command"),
  ],
  [
    "grep",
    (record) => {
      const pattern =
        toolInput.str(record.pattern) ?? toolInput.str(record.query);
      return labelOnly(
        pattern ? `Searched "${toolInput.truncate(pattern, 60)}"` : "Searched",
      );
    },
  ],
  [
    "glob",
    (record) => {
      const pattern =
        toolInput.str(record.pattern) ?? toolInput.str(record.glob);
      return labelOnly(
        pattern
          ? `Matched ${toolInput.truncate(pattern, 60)}`
          : "Matched files",
      );
    },
  ],
  [
    "ls",
    (record) => {
      const path = toolInput.str(record.path) ?? toolInput.str(record.dir);
      return labelOnly(path ? `Listed ${path}` : "Listed a directory");
    },
  ],
  [
    "webfetch",
    (record) => {
      const host = toolActivityHostname(toolInput.str(record.url));
      return labelOnly(host ? `Fetched ${host}` : "Fetched a page");
    },
  ],
  [
    "websearch",
    (record) => {
      const query = toolInput.str(record.query) ?? toolInput.str(record.q);
      return labelOnly(
        query
          ? `Searched the web for "${toolInput.truncate(query, 60)}"`
          : "Searched the web",
      );
    },
  ],
  ["task", () => labelOnly("Ran a subagent")],
  [
    HOST_TOOL_NAMES.SET_STEPS,
    (record) => {
      const steps = Array.isArray(record.steps) ? record.steps.length : null;
      return labelOnly(
        steps
          ? `Planned ${steps} step${steps === 1 ? "" : "s"}`
          : "Planned the work",
      );
    },
  ],
  [
    HOST_TOOL_NAMES.SET_TODOS,
    (record) => {
      const step = toolInput.trimmed(record.step);
      return labelOnly(step ? `Updated the plan: ${step}` : "Updated the plan");
    },
  ],
  [
    HOST_TOOL_NAMES.SAVE_DEV_ENV_MANIFEST,
    () => labelOnly("Saved the dev environment manifest"),
  ],
  [
    HOST_TOOL_NAMES.REQUEST_DEV_ENV_CREDENTIALS,
    (record) => {
      const asked = Array.isArray(record.credentials)
        ? record.credentials.length
        : null;
      return labelOnly(
        asked
          ? `Requested ${asked} credential${asked === 1 ? "" : "s"}`
          : "Requested credentials",
      );
    },
  ],
  [HOST_TOOL_NAMES.REQUEST_ENV_VARS, describeRequestEnvVars],
  [
    HOST_TOOL_NAMES.GENERATE_DEV_ENV_SECRET,
    (record) =>
      labelOnly(
        typeof record.name === "string"
          ? `Generated ${record.name}`
          : "Generated a dev environment secret",
      ),
  ],
  [
    HOST_TOOL_NAMES.SET_DEV_ENV_SIZING,
    () => labelOnly("Recorded the dev environment sizing"),
  ],
  [HOST_TOOL_NAMES.BAKE_DEV_ENV, () => labelOnly("Baked the dev environment")],
  [
    HOST_TOOL_NAMES.DETECT_PROJECT_DATABASE,
    () => labelOnly("Recorded the project database"),
  ],
  [
    HOST_TOOL_NAMES.BIND_PROJECT_DATABASE,
    () => labelOnly("Requested approval to bind the project database"),
  ],
  [
    HOST_TOOL_NAMES.DATABASE_QUERY,
    (record) => {
      const branch = toolInput.trimmed(record.branch) ?? "production";
      const sql = toolInput.verbatim(record.sql);
      return sql
        ? { text: `Read the database (${branch})`, command: sql }
        : labelOnly(`Read the database (${branch})`);
    },
  ],
  [
    HOST_TOOL_NAMES.DATABASE_WRITE,
    (record) => {
      const branch = toolInput.trimmed(record.branch) ?? "production";
      const sql = toolInput.verbatim(record.sql);
      return sql
        ? {
            text: `Requested approval to write to the database (${branch})`,
            command: sql,
          }
        : labelOnly(`Requested approval to write to the database (${branch})`);
    },
  ],
  [HOST_TOOL_NAMES.TAKE_SCREENSHOT, describeTakeScreenshot],
  [HOST_TOOL_NAMES.RECORD_VIDEO, describeRecordVideo],
  [HOST_TOOL_NAMES.ATTACH_IMAGE, describeAttachImage],
  [HOST_TOOL_NAMES.ATTACH_FILE, describeAttachFile],
  [
    HOST_TOOL_NAMES.GET_IMAGE_GENERATORS,
    () => labelOnly("Listed the image models"),
  ],
  [
    HOST_TOOL_NAMES.ASK_USER,
    (record) => {
      const asked = Array.isArray(record.questions)
        ? record.questions.length
        : null;
      return labelOnly(
        asked
          ? `Asked ${asked} question${asked === 1 ? "" : "s"}`
          : "Asked a question",
      );
    },
  ],
  [
    HOST_TOOL_NAMES.PROPOSE_PLAN,
    (record) => {
      const title = toolInput.trimmed(record.title);
      return labelOnly(
        title
          ? `Proposed a plan: ${toolInput.truncate(title, 60)}`
          : "Proposed a plan",
      );
    },
  ],
  [
    HOST_TOOL_NAMES.REQUEST_GUARDRAIL_APPROVAL,
    () => labelOnly("Asked for an approval"),
  ],
  [
    HOST_TOOL_NAMES.REPORT_CONFIDENCE,
    () => labelOnly("Reported its confidence"),
  ],
  [
    HOST_TOOL_NAMES.REPORT_HARNESS_ISSUE,
    (record) => {
      const title = toolInput.trimmed(record.title);
      return labelOnly(
        title
          ? `Reported a harness issue: ${toolInput.truncate(title, 60)}`
          : "Reported a harness issue",
      );
    },
  ],
  [HOST_TOOL_NAMES.MEMORY_SEARCH, describeMemorySearch],
  [HOST_TOOL_NAMES.REPORT_DEV_SERVER, describeReportDevServer],
  [HOST_TOOL_NAMES.MEMORY_WRITE, () => labelOnly("Wrote to its memory")],
  [
    HOST_TOOL_NAMES.PROJECT_CONTEXT_READ,
    () => labelOnly("Read the project's shared context"),
  ],
  [
    HOST_TOOL_NAMES.PROJECT_CONTEXT_WRITE,
    (record) => {
      const title = toolInput.verbatim(record.title);
      return title
        ? labelOnly(
            `Wrote to the project context: ${toolInput.truncate(title, 60)}`,
          )
        : labelOnly("Wrote to the project context");
    },
  ],
  [
    HOST_TOOL_NAMES.RUN_VALIDATION,
    (record) => {
      const command = toolInput.verbatim(record.command);
      return command
        ? { text: "Ran a validation command", command }
        : labelOnly("Ran a validation command");
    },
  ],
  [
    HOST_TOOL_NAMES.CREATE_TASK,
    (record) => {
      const title = toolInput.trimmed(record.title);
      return labelOnly(
        title
          ? `Created task "${toolInput.truncate(title, 60)}"`
          : "Created a team task",
      );
    },
  ],
  [HOST_TOOL_NAMES.LIST_TASKS, () => labelOnly("Listed the team tasks")],
  [HOST_TOOL_NAMES.WORKFLOW, describeWorkflow],
  [HOST_TOOL_NAMES.PICKUP_TASK, () => labelOnly("Picked up a team task")],
  [
    HOST_TOOL_NAMES.UPDATE_TASK,
    (record) => {
      const action = toolInput.str(record.action);
      return labelOnly(
        (action ? updateTaskLabels.get(action) : null) ?? "Updated a team task",
      );
    },
  ],
  [
    HOST_TOOL_NAMES.DELIVERABLE_PUBLISH,
    () => labelOnly("Published a deliverable"),
  ],
  [
    HOST_TOOL_NAMES.RENAME_SESSION,
    (record) => {
      const title = toolInput.trimmed(record.title);
      return labelOnly(
        title
          ? `Renamed the chat "${toolInput.truncate(title, 60)}"`
          : "Renamed the chat",
      );
    },
  ],
  [HOST_TOOL_NAMES.GENERATE_IMAGE, describeGenerateImage],
  [HOST_TOOL_NAMES.ANNOTATE_IMAGE, describeAnnotateImage],
  ...integrationToolActivityDescribers,
  ...browserToolActivityDescribers,
  ...designToolActivityDescribers,
  ...factoryToolActivityDescribers,
]);
